import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Col, Grid, Row } from 'react-bootstrap';
import { Form } from 'react-final-form';
import { retrieveProject, selectProject } from '../../../modules/projects.js';
import { getMultipleEditorsData } from '../../../components/editor/defaultOptions.js';
import {
  navigateToChangeStrategy,
  navigateToStepGeneral,
  navigateToStepGovernance,
  onReset,
} from './utils/index.js';
import { mapFinalFormErrors } from '../../../lib/utils.js';
import StepDetailsForm from '../../../components/projects/ChangeStrategy/ChnageStrategyForms/StepDetailsForm.jsx';
import {
  deleteChangeStrategyDraft,
  selectChangeStrategy,
  selectDraftChangeStrategy,
  updateChangeStrategy,
  updateChangeStrategyDraft,
} from '../../../modules/changeStrategy.js';
import { searchUsers } from '../../../modules/users.js';
import { retrieveProjectOwners } from '../../../modules/projectOwners.js';
import ChangeStrategyFormHeader from '../../../components/headers/PageHeaders/Project/ChangeStrategy/ChangeStrategyFormHeader.jsx';
import MultistepCombinedHeader from '../../../components/headers/FormHeaders/MultistepCombinedHeader.jsx';
import useFormInitialValues from '../../../hooks/useFormInitialValues.js';

const mapErrors = mapFinalFormErrors('Failed to save change strategy');

const mapStateToProps = (state, ownProps) => ({
  editing: !!state.changeStrategy.changeStrategy,
  changeStrategy: selectChangeStrategy(state),
  changeStrategyDraft: selectDraftChangeStrategy(state),
  projectId: ownProps.projectId,
  project: selectProject(ownProps.projectId)(state),
});

const mapDispatchToProps = {
  retrieveProject,
  retrieveProjectOwners,
  updateChangeStrategyDraft,
  updateChangeStrategy,
  deleteChangeStrategyDraft,
  searchUsers,
};

const StepDetailsFormContainer = ({
  slug,
  changeStrategyDraft,
  changeStrategy,
  projectId,
  project,
  editing,
  retrieveProjectOwners,
  updateChangeStrategyDraft,
  updateChangeStrategy,
  deleteChangeStrategyDraft,
}) => {
  const [fields, setFields] = useState(null);
  const [loading, setLoading] = useState(false);

  const goNext = () => navigateToStepGovernance(editing, projectId, slug);
  const goBack = () => navigateToStepGeneral(editing, projectId, slug);
  const goStart = () => navigateToChangeStrategy(projectId, slug);
  const mergedInitialValues = changeStrategyDraft
    ? { ...(changeStrategy || {}), ...changeStrategyDraft }
    : null;
  const initialValues = useFormInitialValues(mergedInitialValues);

  const generateFields = () => {
    const filteredFields = project.changeStrategyFields
      .filter((field) => field.allowEditor)
      .sort((a, b) => Number(a.priority) - Number(b.priority));

    const fields = filteredFields.map((item) => ({
      label: item.name,
      key: item.key,
      isEditor: item.editor,
      editorInstance: null,
    }));
    setFields(fields);
  };

  const updateEditorInstance = (editorObject, index) => {
    setFields((prevFields) => {
      const updatedObj = { ...prevFields[index], editorInstance: editorObject };
      return [
        ...prevFields.slice(0, index),
        updatedObj,
        ...prevFields.slice(index + 1),
      ];
    });
  };

  const onSubmit = (values) => {
    const { finish } = values;
    setLoading(true);
    const editorInstances = fields.filter((field) => field.editorInstance);

    const composedData = (editorData) => {
      const editorFields = editorInstances.reduce((acc, item, index) => {
        acc[item.key] = JSON.stringify(editorData[index]);
        return acc;
      }, {});
      const nonEditorFields = fields
        .filter((field) => !field.isEditor)
        .reduce((acc, item) => {
          acc[item.key] = values[item.key] || null;
          return acc;
        }, {});

      return {
        ...nonEditorFields,
        ...editorFields,
      };
    };

    return getMultipleEditorsData(
      editorInstances.map((item) => item.editorInstance)
    )
      .then((editorData) => {
        const draftData = composedData(editorData);
        if (Object.keys(draftData).length > 0) {
          if (finish) {
            updateChangeStrategy(
              projectId,
              { change_strategy: draftData },
              true,
              true
            ).then(() => {
              goStart();
            });
          } else {
            updateChangeStrategyDraft(
              projectId,
              { change_strategy: draftData },
              true,
              true
            ).then(() => {
              retrieveProjectOwners().then(() => {
                goNext();
                setLoading(false);
              });
            });
          }
        } else if (finish) {
          goStart();
        } else {
          retrieveProjectOwners().then(() => {
            goNext();
            setLoading(false);
          });
        }
      })

      .catch(mapErrors)
      .finally(() => {});
  };

  const onCancel = () => {
    deleteChangeStrategyDraft(projectId).then(() => {
      navigateToChangeStrategy(projectId, slug);
    });
  };

  useEffect(() => {
    !changeStrategyDraft && onReset(editing, projectId, slug);
  }, []);

  useEffect(() => {
    if (project && !fields) {
      generateFields();
    }
  }, [project]);

  return (
    <React.Fragment>
      <MultistepCombinedHeader
        editing={editing}
        subject="Change Strategy"
        typeStep="Details"
      >
        {project && (
          <ChangeStrategyFormHeader
            slug={slug}
            project={project}
            editing={editing}
          />
        )}
      </MultistepCombinedHeader>
      <Grid>
        <Row>
          <Col sm={12}>
            <div className="component-xl component-bg-white component-xl-padding-vertical component-no-horizontal-padding">
              {project && changeStrategyDraft ? (
                <Row>
                  <Col sm={10} smOffset={1}>
                    <Form
                      onSubmit={onSubmit}
                      onCancel={onCancel}
                      loading={loading}
                      initialValues={initialValues}
                      updateEditorInstance={updateEditorInstance}
                      fields={fields || []}
                      onBack={goBack}
                      component={StepDetailsForm}
                    />
                  </Col>
                </Row>
              ) : null}
            </div>
          </Col>
        </Row>
      </Grid>
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(StepDetailsFormContainer);
